import { useState, useCallback } from 'react';

import toast from 'components/partials/toast/toast';
import type { ActionsDropdownOption } from 'components/partials/actions-dropdown/actions-dropdown';

import {
  executeModalAction,
  buildGroupActions as buildGroupActionsHelper,
  buildRegimeActions as buildRegimeActionsHelper,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-helpers';
import type {
  GroupWithRegimes,
  ModalState,
  RegimeGroup,
  RegimeItem,
} from 'containers/admin/clients/client/tabs-components/aid-studio-tab/aid-studio-details/steps/matrix-step/hooks/regime-sidebar-types';

interface PendingMutation {
  isPending: boolean;
}

interface MutationLike extends PendingMutation {
  mutateAsync(data: unknown): Promise<unknown>;
}

interface UseRegimesSidebarModalActionsParams {
  userId: string;
  modelId: string | number;
  mutations: Record<string, MutationLike>;
  groupsWithRegimes: GroupWithRegimes[];
  onRegimeMove: (regimeId: string | number, direction: 'up' | 'down') => void;
}

interface UseRegimesSidebarModalActionsResult {
  modalState: ModalState | null;
  isModalLoading: boolean;
  handleModalConfirm: (arg?: string | number | null) => Promise<void>;
  buildGroupActions: (group: RegimeGroup) => ActionsDropdownOption[];
  buildRegimeActions: (regime: RegimeItem) => ActionsDropdownOption[];
  closeModal: () => void;
}

export const useRegimesSidebarModalActions = ({
  userId,
  modelId,
  mutations,
  groupsWithRegimes,
  onRegimeMove,
}: UseRegimesSidebarModalActionsParams): UseRegimesSidebarModalActionsResult => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const handleModalConfirm = useCallback(
    async (arg?: string | number | null) => {
      if (!modalState) return;

      try {
        await executeModalAction({
          type: modalState.type,
          entity: modalState.entity,
          arg,
          mutations,
          userId,
          modelId,
        });

        setModalState(null);
      } catch (error) {
        const err = error as { data?: { detail?: string } };

        toast.error({
          title: 'Error',
          message:
            err?.data?.detail || 'Something went wrong. Please try again.',
        });
      }
    },
    [modalState, mutations, userId, modelId]
  );

  const isModalLoading = Object.values(mutations).some(
    (mutation) => mutation.isPending
  );

  const buildGroupActions = useCallback(
    (group: RegimeGroup) =>
      buildGroupActionsHelper(
        group as unknown as Record<string, unknown>,
        setModalState as (state: unknown) => void
      ) as ActionsDropdownOption[],
    []
  );

  const buildRegimeActions = useCallback(
    (regime: RegimeItem) => {
      const group = groupsWithRegimes.find((g) => g.id === regime.groupId);
      const idx = group?.regimes.findIndex((r) => r.id === regime.id) ?? -1;
      const groupRegimeCount = group?.regimes.length ?? 0;

      return buildRegimeActionsHelper(
        regime as unknown as Record<string, unknown>,
        setModalState as (state: unknown) => void,
        {
          onMoveUp: () => onRegimeMove(regime.id, 'up'),
          onMoveDown: () => onRegimeMove(regime.id, 'down'),
          isMoveUpDisabled: idx <= 0,
          isMoveDownDisabled: idx < 0 || idx >= groupRegimeCount - 1,
        }
      ) as ActionsDropdownOption[];
    },
    [groupsWithRegimes, onRegimeMove]
  );

  return {
    modalState,
    isModalLoading,
    handleModalConfirm,
    buildGroupActions,
    buildRegimeActions,
    closeModal: useCallback(() => setModalState(null), []),
  };
};
